import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ApolloError } from "@apollo/client/errors";
import { useForm, Controller } from "react-hook-form";
import { DateTime } from "luxon";

import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Radio from "@/components/Radio";
import Select from "@/components/Select";
import Switch from "@/components/Switch";
import TextField from "@/components/TextField";

import { stateLayersColors, sysColors } from "@/assets/theme/colors";

import { WorkingLocation } from "@/types";
import { JobTitle, TourCode, EmploymentRow } from "../../person/queries";
import { A3TourCodeField } from "../../person/_components/A3TourCodeField";
import { FacilityAutocomplete } from "../../person/_components/FacilityAutocomplete";
import MenuDropdown from "@/components/MenuDropdown";
import SingleDatePicker from "../../person/_components/SingleDatePicker";
import { showSnackbar } from "@/lib/snackbarUtils";

type EmploymentAction = "keep" | "end" | null;

export type FieldValues = {
  classification: string;
  endDate: string;
  facilityId: string;
  isPrimary: boolean;
  startDate: string;
  tourCode?: string;
  workingLocation: WorkingLocation;
  note: string;
  employmentsToEnd?: {
    id: string;
    endedOn: string;
  }[];
};

export type EmploymentFormData = {
  endedOn?: string | null;
  facilityId: string;
  isPrimary: boolean;
  jobClassification: string;
  jobTitle: string;
  startedOn: string;
  tourCode?: string | null;
  workingLocation: WorkingLocation;
  note?: string;
  employmentsToEnd?: {
    id: string;
    endedOn: string;
  }[];
};

interface EmploymentFormModalProps {
  pgaId?: string;
  open?: boolean;
  onClose(): void;
  values?: FieldValues;
  headline: string;
  availableJobTitles: JobTitle[];
  tourCodes?: TourCode[];
  onSubmit(formData: EmploymentFormData): Promise<{ success: boolean; message: string }>;
  employments?: EmploymentRow[];
}

const workingLocationOptions = [
  { label: "On-site", value: WorkingLocation.ON_SITE },
  { label: "Remote", value: WorkingLocation.REMOTE }
];

const defaultValues: FieldValues = {
  facilityId: "",
  isPrimary: false,
  classification: "",
  startDate: "",
  endDate: "",
  workingLocation: WorkingLocation.ON_SITE,
  tourCode: "",
  note: "",
  employmentsToEnd: []
};

/**
 * Checks if an employment is active (has not ended)
 */
const isActiveEmployment = (employment: { endedOn: string | null }): boolean => {
  if (!employment.endedOn) return true;
  const endedDate = DateTime.fromISO(employment.endedOn);
  return !endedDate.isValid || endedDate > DateTime.now();
};

const isDateInPastOrToday = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  const date = DateTime.fromISO(dateString);
  if (!date.isValid) return false;
  const today = DateTime.now().startOf("day");
  return date.startOf("day") <= today;
};

export default function EmploymentFormModal({
  open,
  values,
  onClose,
  headline,
  onSubmit,
  availableJobTitles,
  tourCodes = [],
  employments = []
}: EmploymentFormModalProps) {
  const jobTitlesOptions =
    availableJobTitles?.map((jobTitle) => ({
      label: `${jobTitle.jobClassification} - ${jobTitle.jobTitle}`,
      value: jobTitle.id
    })) || [];

  const isA3 = useCallback(
    (classification?: string) =>
      availableJobTitles?.find((job) => job.id === classification)?.jobClassification === "A-3",
    [availableJobTitles]
  );

  const [bannerError, setBannerError] = useState("");
  const [employmentActions, setEmploymentActions] = useState<EmploymentAction[]>([]);
  const { isPrimary: initialIsPrimary } = values || {};

  const activeEmployments = useMemo(() => {
    return employments.filter(isActiveEmployment);
  }, [employments]);

  useEffect(() => {
    setEmploymentActions(activeEmployments.map(() => null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEmployments.length]);

  // Determine if this is a new employment (no values provided) and should default to primary
  const isNewEmployment = !values;

  // Only disable when editing an existing primary employment
  // For new employments with 0 active, we'll prevent turning off via onChange instead
  const shouldDisablePrimary = !!initialIsPrimary;

  // Calculate initial isPrimary value - if new employment, default to true (whether or not there are active employments)
  const initialIsPrimaryValue = useMemo(() => {
    if (values?.isPrimary !== undefined) {
      return values.isPrimary;
    }
    return isNewEmployment ? true : defaultValues.isPrimary;
  }, [values?.isPrimary, isNewEmployment]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FieldValues>({
    defaultValues: {
      ...defaultValues,
      isPrimary: initialIsPrimaryValue
    },
    values: values
      ? {
          ...values,
          isPrimary: values.isPrimary ?? initialIsPrimaryValue
        }
      : undefined,
    mode: "onChange"
  });

  const employmentEndDates = activeEmployments.map((_, index) => watch(`employmentsToEnd.${index}.endedOn`));

  const shouldDefaultToPrimary = useMemo(() => {
    if (!isNewEmployment) return false;

    if (activeEmployments.length === 0) return true;

    return activeEmployments.some((employment, index) => {
      if (!employment.isPrimary) return false;
      const endedOnValue = employmentEndDates[index];
      return !!endedOnValue;
    });
  }, [isNewEmployment, activeEmployments, employmentEndDates]);

  // Set isPrimary to true when creating a new employment with 0 active employments
  // or when a primary active employment has an end date filled in
  // and ensure it stays true (cannot be turned off)
  useEffect(() => {
    if (shouldDefaultToPrimary) {
      setValue("isPrimary", true, { shouldValidate: true });
    }
  }, [shouldDefaultToPrimary, setValue]);

  // Watch isPrimary value to ensure it stays true when shouldDefaultToPrimary is true
  const isPrimaryValue = watch("isPrimary");

  // Disable when creating a new employment with 0 active employments and toggle is on
  const shouldDisablePrimaryForNewEmployment = useMemo(() => {
    return isNewEmployment && activeEmployments.length === 0 && isPrimaryValue === true;
  }, [isNewEmployment, activeEmployments.length, isPrimaryValue]);

  const classification = watch("classification", values?.classification);
  const tourCode = watch("tourCode", values?.tourCode);
  const endDate = watch("endDate");
  const isA3Classification = useMemo(() => isA3(classification), [classification, isA3]);
  const endDateIsInPastOrToday = useMemo(() => isDateInPastOrToday(endDate), [endDate]);

  useEffect(() => {
    if (endDateIsInPastOrToday && isPrimaryValue) {
      setValue("isPrimary", false, { shouldValidate: true });
    }
  }, [endDateIsInPastOrToday, isPrimaryValue, setValue]);

  const currentTourOptions = useMemo(() => {
    return tourCodes.filter((tour) => tour.current) || [];
  }, [tourCodes]);

  useEffect(() => {
    if (tourCode) {
      const facilityId = currentTourOptions.find((tour) => tour.code === tourCode)?.facility?.id;

      if (facilityId) {
        setValue("facilityId", facilityId);
      }
    }
  }, [tourCode, currentTourOptions, setValue]);

  const showActiveEmployments = activeEmployments.length > 0 && isPrimaryValue;

  const allEmploymentActionsChosen = useMemo(() => {
    if (!showActiveEmployments) return true;
    return employmentActions.every((action) => action !== null);
  }, [showActiveEmployments, employmentActions]);

  const allEndDatesProvided = useMemo(() => {
    if (!showActiveEmployments) return true;
    return employmentActions.every((action, index) => {
      if (action !== "end") return true;
      const endedOn = employmentEndDates[index];
      return !!endedOn;
    });
  }, [showActiveEmployments, employmentActions, employmentEndDates]);

  const activeEmploymentsBannerText = useMemo(() => {
    if (!showActiveEmployments) return null;
    if (!allEmploymentActionsChosen) return "Scroll down to choose an action for each active employment.";
    if (!allEndDatesProvided) return "Scroll down to provide an end date for each employment being ended.";
    return null;
  }, [showActiveEmployments, allEmploymentActionsChosen, allEndDatesProvided]);

  // Re-entry guard to prevent duplicate employment rows when the Save button is
  // clicked or activated (Enter) more than once before React disables it.
  // RHF's isSubmitting + button `disabled` are not synchronous enough on their
  // own against rapid double-fires. See: duplicate active employments cleanup.
  const submittingRef = useRef(false);

  async function submit({ facilityId, isPrimary, workingLocation, tourCode, note, ...formData }: FieldValues) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const job = availableJobTitles?.find((j) => j.id === formData.classification);

      if (!job) throw new Error("classification required");
      const startedOn = formData.startDate;
      const endedOn = formData.endDate;
      const employmentsToEnd = isPrimary
        ? activeEmployments
            .map((employment, index) => ({
              id: employment.id,
              endedOn: employmentActions[index] === "end" ? formData.employmentsToEnd?.[index]?.endedOn : undefined
            }))
            .filter((e): e is { id: string; endedOn: string } => !!e.endedOn)
        : [];
      if (!startedOn) throw new Error("Start date is required");
      const { jobTitle, jobClassification } = job;
      const tourPlayer = jobClassification === "A-3";
      const trimmedTourCode = typeof tourCode === "string" ? tourCode.trim() : "";

      const { success, message } = await onSubmit({
        endedOn: endedOn || null,
        facilityId,
        isPrimary,
        jobClassification,
        jobTitle,
        startedOn,
        workingLocation,
        employmentsToEnd,
        // Use jobClassification from the resolved job (not watch-derived isA3Classification) so the
        // payload cannot omit tourCode when the selected title is A-3 (avoids RHF/watch timing edge cases).
        tourCode: tourPlayer ? trimmedTourCode || null : null,
        note: note?.trim() || undefined
      });

      if (success) {
        showSnackbar(message);
        onClose();
      } else {
        throw new Error(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    } finally {
      submittingRef.current = false;
    }
  }

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[648px] max-w-[90vw]"
      open={open}
      onClosed={onClose}
      headline={headline}
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="add-employment-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submit)}
            testId="add-employment-save-button"
            disabled={isSubmitting || !isValid || !allEmploymentActionsChosen || !allEndDatesProvided}
          >
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The update failed." subText={bannerError} />
        </div>
      )}
      {!!activeEmploymentsBannerText && (
        <div className="mb-4">
          <Banner inline variant="warn" description={activeEmploymentsBannerText} />
        </div>
      )}

      <div className="flex flex-col gap-6" data-testid="create-employment-modal">
        <FacilityAutocomplete
          control={control}
          label="Search Facility"
          name="facilityId"
          disabled={isA3Classification}
        />
        <div className="flex items-center justify-between">
          <div>
            <div id="primary-facility-label" className="text-body-large text-sys-brand-on-surface">
              Primary Facility
            </div>
            <div className="text-body-medium text-sys-brand-on-surface-variant">
              Note that one facility must be primary
            </div>
          </div>
          <Controller
            name="isPrimary"
            control={control}
            render={({ field: { onChange, value } }) => {
              // When there are 0 active employments or a primary employment has an end date,
              // force the switch to be ON
              // Use the watched value to ensure we have the latest form state
              // If shouldDefaultToPrimary is true, always show as ON regardless of form value
              // But if endDateIsInPastOrToday, the switch should be OFF and disabled
              const currentValue = endDateIsInPastOrToday
                ? false
                : shouldDefaultToPrimary
                  ? true
                  : isPrimaryValue !== undefined
                    ? isPrimaryValue
                    : value;

              // Apply custom color when disabled
              // - for new employment with 0 active employments, or
              // - when end date is in the past
              const shouldApplyDisabledStyle = shouldDisablePrimaryForNewEmployment || endDateIsInPastOrToday;
              const switchStyle = shouldApplyDisabledStyle
                ? ({
                    "--md-switch-handle-color": sysColors["sys-brand-outline"],
                    "--md-switch-track-color": sysColors["sys-brand-surface-container-highest"],
                    "--md-switch-selected-handle-color": stateLayersColors["state-layers-brand-primary-opacity-0-12"],
                    "--md-switch-selected-track-color": stateLayersColors["state-layers-brand-primary-opacity-0-12"]
                  } as React.CSSProperties)
                : undefined;

              const isDisabled = shouldDisablePrimary || shouldDisablePrimaryForNewEmployment || endDateIsInPastOrToday;

              return (
                <Switch
                  disabled={isDisabled}
                  aria-labelledby="primary-facility-label"
                  selected={currentValue}
                  style={switchStyle}
                  onChange={() => {
                    // Prevent turning off when:
                    // - there are 0 active employments (must stay primary), or
                    // - a primary active employment has an end date filled in
                    // Also prevent onChange when disabled (editing existing primary)
                    // Also prevent when end date is in past
                    if (endDateIsInPastOrToday) {
                      // Keep it OFF when end date is in past
                      onChange(false);
                    } else if (shouldDefaultToPrimary) {
                      // Force it to stay ON - don't allow turning off
                      onChange(true);
                    } else if (!isDisabled) {
                      // Use the actual form field value, not the computed display value
                      onChange(!value);
                    }
                  }}
                />
              );
            }}
          />
        </div>

        <Controller
          name="classification"
          control={control}
          rules={{ required: "Classification is required" }}
          render={({ field: { onChange, value } }) => (
            <MenuDropdown
              options={jobTitlesOptions}
              value={jobTitlesOptions.find((j) => j.value === value)}
              onSelect={(option) => onChange(option.value)}
              label="Classification"
              getLabel={(option) => option.label}
              renderItem={(option) => (
                <div className="flex items-start gap-3 w-full" data-testid={`classification-option-${option.value}`}>
                  <div className="flex-1">
                    <div className="text-body-large">{option.label}</div>
                  </div>
                </div>
              )}
            />
          )}
        />

        {isA3Classification && (
          <A3TourCodeField control={control} currentTourOptions={currentTourOptions} errors={errors} />
        )}

        <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
          <SingleDatePicker control={control} label="Start Date" name="startDate" required useModal />
          <SingleDatePicker control={control} label="End Date" name="endDate" useModal />
        </div>

        <Controller
          name="workingLocation"
          control={control}
          rules={{ required: "Working location is required" }}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Working Location"
              options={workingLocationOptions}
              value={value}
              onSelect={onChange}
              error={!!errors.workingLocation}
              errorText={errors.workingLocation?.message}
            />
          )}
        />

        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={4}
              label="Internal Note"
              value={value || ""}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />

        {showActiveEmployments && (
          <>
            <div className="border-t border-sys-brand-outline-variant" />
            <p className="text-body-large text-sys-brand-on-surface">Choose what to do with each active employment:</p>
            {activeEmployments.map((e: EmploymentRow, i) => {
              const displayTitle = e.formattedTitle || e.title;
              const label = `${e.jobClassification} - ${displayTitle} (${e.facility.name})`;
              const action = employmentActions[i];

              return (
                <div
                  key={e.id}
                  className="flex flex-col gap-3 rounded-lg border border-sys-brand-outline-variant p-4"
                  data-testid={`employment-action-${i}`}
                >
                  <div className="text-body-large-semibold text-sys-brand-on-surface">{label}</div>
                  <div className="flex flex-col gap-2">
                    <Radio
                      label="Keep Active"
                      name={`employment-action-${i}`}
                      id={`employment-action-${i}-keep`}
                      checked={action === "keep"}
                      onChange={(checked) => {
                        if (checked) {
                          setEmploymentActions((prev) => {
                            const next = [...prev];
                            next[i] = "keep";
                            return next;
                          });
                          setValue(`employmentsToEnd.${i}.endedOn`, "", { shouldValidate: true });
                        }
                      }}
                    />
                    <Radio
                      label="End Employment"
                      name={`employment-action-${i}`}
                      id={`employment-action-${i}-end`}
                      checked={action === "end"}
                      onChange={(checked) => {
                        if (checked) {
                          setEmploymentActions((prev) => {
                            const next = [...prev];
                            next[i] = "end";
                            return next;
                          });
                        }
                      }}
                    />
                    {action === "end" && (
                      <div className="pl-8 pt-1">
                        <SingleDatePicker
                          control={control}
                          label="End Date"
                          name={`employmentsToEnd.${i}.endedOn`}
                          key={`employmentsToEnd.${i}.endedOn`}
                          useModal
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </Dialog>
  );
}
